// src/FAQ.js
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import "./FAQ.css";
import logo from "./assets/vidigitalcodelogo.png";
import costIcon from "./assets/cost.png";
import websiteOnPhoneIcon from "./assets/websiteonphone.png";
import maintenanceIcon from "./assets/maintenance.png";
import socialMediaIcon from "./assets/socialmedia.png";
import timeIcon from "./assets/time.png";
import diyIcon from "./assets/diy.png";
import emailSetupIcon from "./assets/emailsetup.png";
import breakage from './assets/break.png';

function FAQ() {
  const [activeScene, setActiveScene] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const navigate = useNavigate();

  const [visibleScene, setVisibleScene] = useState(0);
  const videoRefs = useRef([]);

  // Handle video autoplay when scene comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleScene(index);

            // Try to play the video if it exists
            const videoIframe = entry.target.querySelector('iframe');
            if (videoIframe) {
              // Force autoplay by updating src
              const src = videoIframe.src;
              if (!src.includes('autoplay=true')) {
                const newSrc = src.includes('?')
                  ? `${src}&autoplay=true`
                  : `${src}?autoplay=true`;
                videoIframe.src = newSrc;
              }
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.5 // 50% visible
      }
    );

    // Observe all pinned cards
    const cards = document.querySelectorAll('.pinned-card');
    cards.forEach((card, index) => {
      card.dataset.index = index;
      observer.observe(card);
    });

    return () => {
      cards.forEach(card => observer.unobserve(card));
    };
  }, []);


  const getVideoUrl = (videoId) => {
    return `https://customer-egd8wletnhjj8i2u.cloudflarestream.com/${videoId}/iframe?autoplay=true&loop=true&muted=true&controls=false&preload=auto`;
  };

  // Scroll to top on mount for better UX
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Show/hide back to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle navigation to home sections
  const navigateToSection = (sectionId) => {
    navigate("/");
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };


  const storyboardScenes = [
    {
      id: "scene1",
      title: "What Is a Website?",
      narration: "A website is just your little spot on the internet — like your own house on a street. Inside that house, you can put anything: photos, information, your story… whatever you want people to see.",
      visual: "🏡",
      backgroundColor: "#E1F5FE",
      titleColor: "#01579B",
      subtitleColor: "#0277BD",
      icon: "🏠",
      videoId: "2e4b9dc93e99279143cc27c996f5386b"
    },
    {
      id: "scene2",
      title: "What Is a Domain?",
      narration: "Your domain is just your address — like 123 Main Street. But online, it looks like: yourbusiness.com. It's how people find your house without getting lost.",
      visual: "📍",
      backgroundColor: "#FFF3E0",
      titleColor: "#BF360C",
      subtitleColor: "#D84315",
      icon: "📬",
      videoId: "6a0504fa7dbf422e3400ff55d2967b0e"
    },
    {
      id: "scene3",
      title: "What Is Hosting?",
      narration: "Hosting is simply the land your house sits on. Without land… you can't put your house anywhere. Hosting gives your website a stable place to live.",
      visual: "🌍",
      backgroundColor: "#E8F5E9",
      titleColor: "#1B5E20",
      subtitleColor: "#2E7D32",
      icon: "⚡",
      videoId: "8e997ec172bfc4020be9bba9fcc1ae21"
    },
    {
      id: "scene4",
      title: "How Do People See My Website?",
      narration: "Once you have your house, your address, and your land… your website is officially 'on the map.' Anyone can type your address into their phone or computer and boom — they're at your front door.",
      visual: "🔍",
      backgroundColor: "#E3F2FD",
      titleColor: "#0D47A1",
      subtitleColor: "#1565C0",
      icon: "🌐",
      videoId: "773973cb0ccd477d8a56894b6ec4f846"
    },
    {
      id: "scene5",
      title: "Why Do I Need a Website?",
      narration: "A website helps people trust you. It shows you're professional and open for business — even when you're sleeping, cooking dinner, or taking care of the kids. It works for you 24/7.",
      visual: "⭐",
      backgroundColor: "#FFF8E1",
      titleColor: "#FF6F00",
      subtitleColor: "#FF8F00",
      icon: "⏰",
      videoId: "4cbc027b1ec90985b0fb2afc91e57970"
    },
    {
      id: "scene6",
      title: "What If I Don't Understand This?",
      narration: "If all of this still sounds confusing — don't worry. That's exactly why I'm here. I handle the tech so you can focus on your business.",
      visual: "🤝",
      backgroundColor: "#F3E5F5",
      titleColor: "#4A148C",
      subtitleColor: "#6A1B9A",
      icon: "🛠️",
      videoId: "c76d1c75e4e764175415ed098203632c"
    }
  ];

  const additionalFAQs = [
    {
      id: "faq6",
      question: "How much does it cost?",
      answer: `• Essential Website Package: Starting at $2,500
             Perfect for simple websites and small, local businesses.
             
             • Professional Business Package: Starting at $4,500–$7,500
             Your most popular package — includes branding, setup, and strategy.
             
             • VIP Digital Partner Package: Starting at $7,500+
             Done-for-you, long-term digital support.
             
             • Care Plans: From $150/mo to $500/mo.`,
      icon: costIcon,
    },
    {
      id: "faq1",
      question: "What if I want changes to my website?",
      answer:
        "Easy. You tell me what you want — I update it. No complicated process, just let me know and I'll make it happen.",
      icon: maintenanceIcon,
    },
    {
      id: "faq2",
      question: "Will my website work on phones?",
      answer:
        "Yep. Everything I build works perfectly on phones, tablets, and computers. Your site will look great no matter how people view it.",
      icon: websiteOnPhoneIcon,
    },
    {
      id: "faq3",
      question: "Do I need social media?",
      answer:
        "Not required. But if you want it — I'll set it up too. I can help you with Facebook, Instagram, or whatever platform makes sense for your business.",
      icon: socialMediaIcon,
    },
    {
      id: "faq4",
      question: "How long does it take to build a website?",
      answer:
        "Most simple websites take 2-4 weeks from start to finish. More complex projects might take longer, but I'll give you a clear timeline upfront.",
      icon: timeIcon,
    },
    {
      id: "faq5",
      question: "What if something breaks?",
      answer:
        "I'm here for you. I offer ongoing support and maintenance, so if something goes wrong or you need help, just reach out.",
      icon: breakage,
    },

    {
      id: "faq7",
      question: "Can I update the website myself?",
      answer:
        "Absolutely! I'll show you how to make simple updates like changing text or adding photos. For bigger changes, I'm always available to help.",
      icon: diyIcon,
    },
    {
      id: "faq8",
      question: "Do you help with email setup?",
      answer:
        "Yes! I can set up professional email addresses (like yourname@yourbusiness.com) and help you manage them all in one place.",
      icon: emailSetupIcon,
    },
  ];
  // ----------------------------
  // Typeless pinned section logic
  // ----------------------------
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  // total number of scenes (we unroll transforms for ESLint safety)
  const N = storyboardScenes.length; // expected 6 in current data

  // === explicit, unrolled useTransform hooks (ESLint/react-hooks safe)
  // Scene 0
  const c0_start = 0 / N;
  const c0_mid = (0 + 0.5) / N;
  const c0_end = (0 + 1) / N;
  const cardOpacity0 = useTransform(scrollYProgress, [c0_start, c0_mid, c0_end], [0, 1, 0]);
  const cardScale0 = useTransform(scrollYProgress, [c0_start, c0_mid, c0_end], [0.85, 1, 0.9]);
  const cardY0 = useTransform(scrollYProgress, [c0_start, c0_mid, c0_end], [50, 0, -50]);

  // Scene 1
  const c1_start = 1 / N;
  const c1_mid = (1 + 0.5) / N;
  const c1_end = (1 + 1) / N;
  const cardOpacity1 = useTransform(scrollYProgress, [c1_start, c1_mid, c1_end], [0, 1, 0]);
  const cardScale1 = useTransform(scrollYProgress, [c1_start, c1_mid, c1_end], [0.85, 1, 0.9]);
  const cardY1 = useTransform(scrollYProgress, [c1_start, c1_mid, c1_end], [50, 0, -50]);

  // Scene 2
  const c2_start = 2 / N;
  const c2_mid = (2 + 0.5) / N;
  const c2_end = (2 + 1) / N;
  const cardOpacity2 = useTransform(scrollYProgress, [c2_start, c2_mid, c2_end], [0, 1, 0]);
  const cardScale2 = useTransform(scrollYProgress, [c2_start, c2_mid, c2_end], [0.85, 1, 0.9]);
  const cardY2 = useTransform(scrollYProgress, [c2_start, c2_mid, c2_end], [50, 0, -50]);

  // Scene 3
  const c3_start = 3 / N;
  const c3_mid = (3 + 0.5) / N;
  const c3_end = (3 + 1) / N;
  const cardOpacity3 = useTransform(scrollYProgress, [c3_start, c3_mid, c3_end], [0, 1, 0]);
  const cardScale3 = useTransform(scrollYProgress, [c3_start, c3_mid, c3_end], [0.85, 1, 0.9]);
  const cardY3 = useTransform(scrollYProgress, [c3_start, c3_mid, c3_end], [50, 0, -50]);

  // Scene 4
  const c4_start = 4 / N;
  const c4_mid = (4 + 0.5) / N;
  const c4_end = (4 + 1) / N;
  const cardOpacity4 = useTransform(scrollYProgress, [c4_start, c4_mid, c4_end], [0, 1, 0]);
  const cardScale4 = useTransform(scrollYProgress, [c4_start, c4_mid, c4_end], [0.85, 1, 0.9]);
  const cardY4 = useTransform(scrollYProgress, [c4_start, c4_mid, c4_end], [50, 0, -50]);

  // Scene 5
  const c5_start = 5 / N;
  const c5_mid = (5 + 0.5) / N;
  const c5_end = (5 + 1) / N;
  const cardOpacity5 = useTransform(scrollYProgress, [c5_start, c5_mid, c5_end], [0, 1, 0]);
  const cardScale5 = useTransform(scrollYProgress, [c5_start, c5_mid, c5_end], [0.85, 1, 0.9]);
  const cardY5 = useTransform(scrollYProgress, [c5_start, c5_mid, c5_end], [50, 0, -50]);

  // Build arrays for mapping (safe; transforms already created)
  const cardOpacities = [cardOpacity0, cardOpacity1, cardOpacity2, cardOpacity3, cardOpacity4, cardOpacity5];
  const cardScales = [cardScale0, cardScale1, cardScale2, cardScale3, cardScale4, cardScale5];
  const cardYs = [cardY0, cardY1, cardY2, cardY3, cardY4, cardY5];

  return (
    <div className="faq-page">
      {/* Navigation */}
      <motion.nav
        className="nav"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ delay: 0.2, type: "spring" }}
      >
        <Link to="/" className="nav-logo">
          <img src={logo} alt="VI Logo" className="nav-logo-icon" />
          <span className="nav-logo-text">Home</span>
        </Link>

        <div className="nav-links">
          <button className="nav-link" onClick={() => navigateToSection("portfolio")}>
            Portfolio
          </button>
          <button className="nav-link" onClick={() => navigateToSection("how-it-works")}>
            Process
          </button>

        </div>

        <motion.button
          className="nav-cta"
          onClick={() => navigateToSection("get-in-touch")}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Book a Call
        </motion.button>
      </motion.nav>

      {/* Breadcrumb Navigation */}
      <motion.div className="breadcrumb" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <Link to="/" className="breadcrumb-link">
          Home
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">FAQ</span>
      </motion.div>

      {/* Hero Section */}
      <section className="faq-hero" style={{ position: "relative", overflow: "hidden" }}>
        {/* Background Video/GIF */}
        <video
          autoPlay
          loop
          muted
          playsInline
          disablePictureInPicture
          disableRemotePlayback
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover", /* Changed from contain to cover */
            zIndex: 0,
          }}
        >
          <source src={require("./assets/portfolio/vidigitalcodewhatisawebsite.mp4")} type="video/mp4" />
        </video>

        {/* Dark Overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1,
          }}
        ></div>

        <motion.div
          className="faq-hero-content"
          style={{
            position: "relative",
            zIndex: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 1rem"
          }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            className="faq-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{ textAlign: "center" }}
          >
            What is a <span className="gradient-text">Website</span> Anyways
          </motion.h1>
          <motion.p
            className="faq-subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{ textAlign: "center" }}
          >
            You might hear 'website' or 'domain' and think it's complicated. It's really not. Let me show you.
          </motion.p>
        </motion.div>
      </section>


      {/*==============================*/}
      {/*  TYPELESS PINNED STORYBOARD  */}
      {/*==============================*/}
      <section className="pinned-storyboard" ref={sectionRef}>
        <div className="pinned-track">
          {storyboardScenes.map((scene, index) => (
            <motion.div
              key={scene.id}
              className="pinned-card"
              style={{
                zIndex: storyboardScenes.length - index,
                opacity: cardOpacities[index],
                scale: cardScales[index],
                y: cardYs[index],
                backgroundColor: scene.backgroundColor,
              }}
            >
              <div className="pinned-card-content">
                <div>
                  <h3 className="pinned-title" style={{ color: scene.titleColor }}>{scene.title}</h3>
                  <p className="pinned-description" style={{ color: scene.subtitleColor }}>{scene.narration}</p>
                </div>

                <div className="pinned-visual" style={{ borderColor: scene.titleColor }}>
                  <iframe
                    src={getVideoUrl(scene.videoId)}
                    style={{
                      border: 'none',
                      width: '100%',
                      height: '100%',
                      borderRadius: '12px',
                    }}
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen={true}
                  ></iframe>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Summary Section */}
      <section className="faq-summary">
        <motion.div className="summary-content" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="section-title">Here's the Simple Version</h2>
          <div className="summary-grid">
            <motion.div className="summary-item" whileHover={{ scale: 1.05 }}>
              <span className="summary-icon">🏠</span>
              <h4>Your Website</h4>
              <p>Is your home</p>
            </motion.div>
            <motion.div className="summary-item" whileHover={{ scale: 1.05 }}>
              <span className="summary-icon">📍</span>
              <h4>Your Domain</h4>
              <p>Is your address</p>
            </motion.div>
            <motion.div className="summary-item" whileHover={{ scale: 1.05 }}>
              <span className="summary-icon">🌍</span>
              <h4>Hosting</h4>
              <p>Is the land</p>
            </motion.div>
            <motion.div className="summary-item" whileHover={{ scale: 1.05 }}>
              <span className="summary-icon">🤝</span>
              <h4>I'm Here</h4>
              <p>To build & maintain it</p>
            </motion.div>
          </div>
          <motion.p className="summary-tagline" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
            Simple, stress-free, and made just for you.
          </motion.p>
        </motion.div>
      </section>

      {/* Additional FAQs */}
      <section className="additional-faqs">
        <motion.div className="faqs-header" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="section-title">More Questions?</h2>
          <p className="section-description">Here are answers to common questions I get</p>
        </motion.div>

        <div className="faqs-grid">
          {additionalFAQs.map((faq, index) => (
            <motion.div
              key={faq.id}
              className={`faq-card ${activeScene === faq.id ? "active" : ""}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              onClick={() => setActiveScene(activeScene === faq.id ? null : faq.id)}
              onKeyPress={(e) => e.key === "Enter" && setActiveScene(activeScene === faq.id ? null : faq.id)}
              whileHover={{ y: -3 }}
              tabIndex={0}
              role="button"
              aria-expanded={activeScene === faq.id}
            >
              <div className="faq-header">
                <div className="faq-icon">
                  <img src={faq.icon} alt="" />
                </div>
                <h3 className="faq-question">{faq.question}</h3>
                <motion.span className="faq-toggle" animate={{ rotate: activeScene === faq.id ? 45 : 0 }}>
                  +
                </motion.span>
              </div>

              <AnimatePresence>
                {activeScene === faq.id && (
                  <motion.div
                    className="faq-answer"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    {faq.id === "faq6" ? (
                      <div style={{ whiteSpace: 'pre-line' }}>{faq.answer}</div>
                    ) : (
                      <p>{faq.answer}</p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="faq-cta">
        <motion.div className="faq-cta-content" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2>Still Have Questions?</h2>
          <p>Let's chat! I'm here to help you understand everything and find the right solution for your business.</p>
          <motion.button className="cta-button" onClick={() => navigateToSection("get-in-touch")} whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }}>
            Let's Talk →
          </motion.button>
        </motion.div>
      </section>


      {/* Back to Top Button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            className="back-to-top"
            onClick={scrollToTop}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Back to top"
          >
            ↑
          </motion.button>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="footer">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
          <p>© 2025 VIdigital • Made with care in the Virgin Islands 🌴</p>
          <p className="footer-quote">Simple, worry-free digital support for your small or large business needs</p>
        </motion.div>
      </footer>
    </div>
  );
}

export default FAQ;




